import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography,
  CircularProgress, TablePagination, Box,
  Chip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button,
  List, ListItem, ListItemText, Divider,
  Tabs, Tab, Badge,
  Tooltip
} from '@mui/material';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import backButton from "../../Images/backButton.svg";
import { ROUTES } from 'src/constant';
import { useNavigate } from 'react-router';

const ViewMessages = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalChats, setTotalChats] = useState(0);
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDateTab, setSelectedDateTab] = useState(0);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [chatDates, setChatDates] = useState<string[]>([]);
  const [selectedChatDateTab, setSelectedChatDateTab] = useState(0);
  const API_URL = process.env.REACT_APP_API_URL;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChats = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${API_URL}/messages` || "https://api.ultramsg.com/instance117611/messages", {
          params: {
            token: API_TOKEN || "ta1aj0gps2ookkv9",
            page: page + 1,
            limit: rowsPerPage
          }
        });

        const messages = response?.data?.messages || [];
        setChats(messages);
        setTotalChats(response?.data?.total || 0);

        const dates = Array.from(new Set(messages.map((chat: any) =>
          format(new Date(chat.sent_at * 1000), 'yyyy-MM-dd')
        ))).sort((a, b) => new Date(b as any).getTime() - new Date(a as any).getTime());

        setChatDates(dates as any);
        setSelectedChatDateTab(0);
      } catch (error) {
        console.error('Failed to fetch chats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [page, rowsPerPage]);

  const fetchChatMessages = async (chatId: string) => {
    try {
      setMessagesLoading(true);

      const response = await axios.get(`${API_URL}/chats/messages` || "https://api.ultramsg.com/instance117611/chats/messages", {
        params: {
          token: API_TOKEN || "ta1aj0gps2ookkv9",
          chatId: chatId,
          limit: 1000
        }
      });

      const messages = response?.data || [];
      setChatMessages(messages);

      // Extract unique dates from messages
      const dates = Array.from(
        new Set(
          messages.map((msg: any) =>
            format(new Date(msg.timestamp * 1000), 'yyyy-MM-dd')
          )
        )
      ).sort((a, b) => new Date(b as any).getTime() - new Date(a as any).getTime());

      setAvailableDates(dates as any);
      setSelectedDateTab(0); // Reset to first tab (all messages)
    } catch (error) {
      console.error('Failed to fetch chat messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if ([25, 50, 100].includes(newRowsPerPage)) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  const handleRowClick = async (chat: any) => {
    setSelectedChat(chat);
    await fetchChatMessages(chat?.to);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedChat(null);
    setChatMessages([]);
    setAvailableDates([]);
  };

  const handleDateTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedDateTab(newValue);
  };

  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d, yyyy');
  };

  const getFilteredMessages = () => {
    if (selectedDateTab === 0) return chatMessages;

    const selectedDate = availableDates[selectedDateTab - 1];
    return chatMessages.filter((msg: any) =>
      format(new Date(msg.timestamp * 1000), 'yyyy-MM-dd') === selectedDate
    );
  };
  const filteredChats = selectedChatDateTab === 0
    ? chats
    : chats.filter((chat: any) =>
      format(new Date(chat.sent_at * 1000), 'yyyy-MM-dd') === chatDates[selectedChatDateTab - 1]
    );
  const handleClose = () => {
    navigate(ROUTES.SEND_BULK_MESSAGES);
  };

  if (loading && chats.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const ackLabelMap: any = {
    read: 'Seen',
    delivered: 'Delivered',
    device: 'Unseen',
    failed: 'Failed',
    pending: 'Sent you',
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        paddingRight={4}
      >
        <Box style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Back">
            <img
              src={backButton}
              alt="backButton"
              height={25}
              width={25}
              style={{ marginRight: "12px", cursor: "pointer" }}
              onClick={handleClose}
            />
          </Tooltip>
          <Typography
            variant="h4"
            gutterBottom
            style={{ color: "#00C5B9" }}
            marginTop={2}
          >
            WhatsApp Messages
          </Typography>
        </Box>
      </Box>
      <TableContainer>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedChatDateTab}
            onChange={(e, newVal) => setSelectedChatDateTab(newVal)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" />
            {chatDates?.map((date, index) => (
              <Tab
                key={date}
                label={
                  <Badge
                    badgeContent={
                      chats.filter((chat: any) =>
                        format(new Date(chat.sent_at * 1000), 'yyyy-MM-dd') === date
                      ).length
                    }
                    color="primary"
                  >
                    {formatDateLabel(date)}
                  </Badge>
                }
              />
            ))}
          </Tabs>
        </Box>
        <Table stickyHeader aria-label="chats table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Message Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChats?.length > 0 ? (
              filteredChats?.map((chat: any, index: number) => (
                <TableRow
                  key={chat?.id}
                  hover
                  onClick={() => handleRowClick(chat)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{chat?.from}</TableCell>
                  <TableCell>{chat?.to}</TableCell>
                  <TableCell>
                    <Chip
                      label={chat?.status}
                      color={
                        chat?.status === 'sent'
                          ? 'success'
                          : chat?.status === 'invalid'
                            ? 'error'
                            : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{new Date(chat?.sent_at * 1000).toLocaleString()}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No chats found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={selectedChatDateTab === 0 ? totalChats : filteredChats?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Chat Messages Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          Chat with {selectedChat?.to}
          <Typography variant="body2" color="text.secondary">
            {getFilteredMessages()?.length} messages
          </Typography>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedDateTab}
            onChange={handleDateTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" />
            {availableDates.map((date, index) => (
              <Tab
                key={date}
                label={
                  <Badge
                    badgeContent={
                      chatMessages.filter((msg: any) =>
                        format(new Date(msg.timestamp * 1000), 'yyyy-MM-dd') === date
                      ).length
                    }
                    color="primary"
                  >
                    {formatDateLabel(date)}
                  </Badge>
                }
              />
            ))}
          </Tabs>
        </Box>

        <DialogContent dividers>
          {messagesLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ width: '100%' }}>
              {getFilteredMessages()?.map((message: any, index) => {
                const messageDate = new Date(message.timestamp * 1000);
                const showDateHeader = index === 0 ||
                  format(new Date(getFilteredMessages()[index - 1].timestamp * 1000), 'yyyy-MM-dd') !==
                  format(messageDate, 'yyyy-MM-dd');

                return (
                  <React.Fragment key={index}>
                    {showDateHeader && (
                      <>
                        <Divider textAlign="left" sx={{ my: 1 }}>
                          <Chip
                            label={formatDateLabel(format(messageDate, 'yyyy-MM-dd'))}
                            size="small"
                          />
                        </Divider>
                      </>
                    )}
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message?.from === selectedChat?.from ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '75%',
                          bgcolor: message?.from === selectedChat?.from ? '#DCF8C6' : '#FFFFFF',
                          borderRadius: 2,
                          p: 1.5,
                          boxShadow: 1,
                          alignSelf: message?.from === selectedChat?.from ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message?.body}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mt={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {message?.from === selectedChat?.from ? 'You' : 'Them'} • {format(messageDate, 'hh:mm a')}
                          </Typography>
                          <Chip
                            label={ackLabelMap[message?.ack] || message?.ack}
                            size="small"
                            sx={{ ml: 1 }}
                            color={
                              message?.ack === 'read'
                                ? 'primary'
                                : message?.ack === 'delivered'
                                ? 'success'
                                : message?.ack === 'device'
                                ? 'default'
                                : message?.ack === 'failed'
                                ? 'error'
                                : message?.ack === 'pending'
                                ? 'warning'
                                : 'info'
                            }
                          />
                        </Box>
                      </Box>
                    </ListItem>

                    {index < getFilteredMessages()?.length - 1 &&
                      format(messageDate, 'yyyy-MM-dd') ===
                      format(new Date(getFilteredMessages()[index + 1].timestamp * 1000), 'yyyy-MM-dd') &&
                      <Divider component="li" />}
                  </React.Fragment>
                );
              })}
              {getFilteredMessages()?.length === 0 && (
                <Typography variant="body1" align="center" sx={{ p: 2 }}>
                  No messages found
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ViewMessages;